/**
 * Session Lock Modal
 * ==================
 * Full-screen modal shown when session is locked due to inactivity.
 * User must enter password to resume their session.
 */

import React, { useState } from 'react';
import { useSecurity } from '../context/SecurityContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/form-input';
import { Shield, Lock, LogOut, AlertCircle } from 'lucide-react';

const SessionLockModal = () => {
  const { isSessionLocked, lockedUser, unlockSession, handleFullLogout } = useSecurity();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isSessionLocked || !lockedUser) {
    return null;
  }

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await unlockSession(password);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Invalid password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    handleFullLogout();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-md mx-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold">Session Locked</h2>
            <p className="text-white/80 text-sm mt-1">
              For your security, your session has been locked due to inactivity
            </p>
          </div>

          {/* Content */}
          <form onSubmit={handleUnlock} className="p-6 space-y-4">
            {/* User info */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                  {lockedUser.name?.charAt(0)?.toUpperCase() || lockedUser.email?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  {lockedUser.name || 'User'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {lockedUser.email}
                </p>
              </div>
            </div>

            {/* Password input */}
            <div className="space-y-2">
              <Label htmlFor="unlock-password">Password</Label>
              <PasswordInput
                id="unlock-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password to resume"
                autoFocus
                disabled={loading}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-3 pt-2">
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                disabled={loading}
                data-testid="unlock-session-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Unlocking...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Unlock Session
                  </span>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleLogout}
                disabled={loading}
                data-testid="logout-from-lock-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 pb-6">
            <p className="text-xs text-center text-slate-500 dark:text-slate-400">
              This security measure helps protect your account from unauthorized access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionLockModal;
